import { type MantineColor } from '@mantine/core';
import { PayinEventType, PublicStatus, Status } from './model';

type OrderStatusMeta = Record<
    Status,
    {
        color: MantineColor;
        label: string;
    }
>;

const orderStatusMeta = {
    // Neutral / Pending
    [Status.STATUS_UNSPECIFIED]: {
        color: 'gray.6',
        label: 'Не определен',
    },
    [Status.STATUS_CREATED]: {
        color: 'gray.6',
        label: 'Создан',
    },
    [Status.STATUS_EXPIRED]: {
        color: 'gray.6',
        label: 'Просрочен',
    },
    // In Progress
    [Status.STATUS_SUGGESTED]: {
        color: 'blue.5',
        label: 'Предложен мерчанту',
    },
    [Status.STATUS_ACQUIRED]: {
        color: 'blue.5',
        label: 'Выбран мерчантом',
    },
    [Status.STATUS_MERCHANT_CONFIRMED]: {
        color: 'blue.5',
        label: 'Мерчант подтвердил перевод',
    },
    [Status.STATUS_PAYOUT_IN_PROCESS]: {
        color: 'blue.5',
        label: 'Выплата компенсации в процессе',
    },
    // Needs Action
    [Status.STATUS_WAITING_FOR_PROOFS]: {
        color: 'orange.5',
        label: 'Ожидает чек от мерчанта',
    },
    [Status.STATUS_WAITING_PROOFS_APPROVAL]: {
        color: 'orange.5',
        label: 'Чек на проверке',
    },
    [Status.STATUS_ON_INVESTIGATION]: {
        color: 'orange.5',
        label: 'На расследовании',
    },
    [Status.STATUS_PAYOUT_INITIATED]: {
        color: 'orange.5',
        label: 'Компенсация инициирована',
    },
    // Success
    [Status.STATUS_TRADER_CONFIRMED]: {
        color: 'green.6',
        label: 'Получение подтверждено',
    },
    [Status.STATUS_ON_INVESTIGATION_APPROVE]: {
        color: 'green.6',
        label: 'Расследование одобрено',
    },
    [Status.STATUS_PAYOUT_COMPLETED]: {
        color: 'green.6',
        label: 'Компенсация выплачена',
    },
    // Failure
    [Status.STATUS_CANCELLED]: {
        color: 'red.6',
        label: 'Отменён трейдером',
    },
    [Status.STATUS_FAILED]: {
        color: 'red.6',
        label: 'Ошибка обработки',
    },
    [Status.STATUS_ON_INVESTIGATION_DECLINED]: {
        color: 'red.6',
        label: 'Расследование отклонено',
    },
    [Status.STATUS_PAYOUT_FAILED]: {
        color: 'red.6',
        label: 'Не удалось выплатить компенсацию',
    },
} as const satisfies OrderStatusMeta;

export const orderStatusColors: Record<Status, MantineColor> = {
    [Status.STATUS_UNSPECIFIED]: orderStatusMeta[Status.STATUS_UNSPECIFIED].color,
    [Status.STATUS_CREATED]: orderStatusMeta[Status.STATUS_CREATED].color,
    [Status.STATUS_CANCELLED]: orderStatusMeta[Status.STATUS_CANCELLED].color,
    [Status.STATUS_SUGGESTED]: orderStatusMeta[Status.STATUS_SUGGESTED].color,
    [Status.STATUS_ACQUIRED]: orderStatusMeta[Status.STATUS_ACQUIRED].color,
    [Status.STATUS_MERCHANT_CONFIRMED]: orderStatusMeta[Status.STATUS_MERCHANT_CONFIRMED].color,
    [Status.STATUS_TRADER_CONFIRMED]: orderStatusMeta[Status.STATUS_TRADER_CONFIRMED].color,
    [Status.STATUS_WAITING_FOR_PROOFS]: orderStatusMeta[Status.STATUS_WAITING_FOR_PROOFS].color,
    [Status.STATUS_WAITING_PROOFS_APPROVAL]: orderStatusMeta[Status.STATUS_WAITING_PROOFS_APPROVAL].color,
    [Status.STATUS_ON_INVESTIGATION]: orderStatusMeta[Status.STATUS_ON_INVESTIGATION].color,
    [Status.STATUS_ON_INVESTIGATION_APPROVE]: orderStatusMeta[Status.STATUS_ON_INVESTIGATION_APPROVE].color,
    [Status.STATUS_ON_INVESTIGATION_DECLINED]: orderStatusMeta[Status.STATUS_ON_INVESTIGATION_DECLINED].color,
    [Status.STATUS_EXPIRED]: orderStatusMeta[Status.STATUS_EXPIRED].color,
    [Status.STATUS_FAILED]: orderStatusMeta[Status.STATUS_FAILED].color,
    [Status.STATUS_PAYOUT_INITIATED]: orderStatusMeta[Status.STATUS_PAYOUT_INITIATED].color,
    [Status.STATUS_PAYOUT_COMPLETED]: orderStatusMeta[Status.STATUS_PAYOUT_COMPLETED].color,
    [Status.STATUS_PAYOUT_IN_PROCESS]: orderStatusMeta[Status.STATUS_PAYOUT_IN_PROCESS].color,
    [Status.STATUS_PAYOUT_FAILED]: orderStatusMeta[Status.STATUS_PAYOUT_FAILED].color,
} as const;

export const orderStatuses = [
    {
        value: Status.STATUS_CREATED,
        label: orderStatusMeta[Status.STATUS_CREATED].label,
    },
    {
        value: Status.STATUS_SUGGESTED,
        label: orderStatusMeta[Status.STATUS_SUGGESTED].label,
    },
    {
        value: Status.STATUS_ACQUIRED,
        label: orderStatusMeta[Status.STATUS_ACQUIRED].label,
    },
    {
        value: Status.STATUS_MERCHANT_CONFIRMED,
        label: orderStatusMeta[Status.STATUS_MERCHANT_CONFIRMED].label,
    },
    {
        value: Status.STATUS_WAITING_FOR_PROOFS,
        label: orderStatusMeta[Status.STATUS_WAITING_FOR_PROOFS].label,
    },
    {
        value: Status.STATUS_WAITING_PROOFS_APPROVAL,
        label: orderStatusMeta[Status.STATUS_WAITING_PROOFS_APPROVAL].label,
    },
    {
        value: Status.STATUS_TRADER_CONFIRMED,
        label: orderStatusMeta[Status.STATUS_TRADER_CONFIRMED].label,
    },
    {
        value: Status.STATUS_ON_INVESTIGATION,
        label: orderStatusMeta[Status.STATUS_ON_INVESTIGATION].label,
    },
    {
        value: Status.STATUS_ON_INVESTIGATION_APPROVE,
        label: orderStatusMeta[Status.STATUS_ON_INVESTIGATION_APPROVE].label,
    },
    {
        value: Status.STATUS_ON_INVESTIGATION_DECLINED,
        label: orderStatusMeta[Status.STATUS_ON_INVESTIGATION_DECLINED].label,
    },
    {
        value: Status.STATUS_PAYOUT_INITIATED,
        label: orderStatusMeta[Status.STATUS_PAYOUT_INITIATED].label,
    },
    {
        value: Status.STATUS_PAYOUT_COMPLETED,
        label: orderStatusMeta[Status.STATUS_PAYOUT_COMPLETED].label,
    },
    {
        value: Status.STATUS_EXPIRED,
        label: orderStatusMeta[Status.STATUS_EXPIRED].label,
    },
    {
        value: Status.STATUS_FAILED,
        label: orderStatusMeta[Status.STATUS_FAILED].label,
    },
    {
        value: Status.STATUS_CANCELLED,
        label: orderStatusMeta[Status.STATUS_CANCELLED].label,
    },
    {
        value: Status.STATUS_PAYOUT_IN_PROCESS,
        label: orderStatusMeta[Status.STATUS_PAYOUT_IN_PROCESS].label,
    },
    {
        value: Status.STATUS_PAYOUT_FAILED,
        label: orderStatusMeta[Status.STATUS_PAYOUT_FAILED].label,
    },
] as const;

export const orderEventTypes: Record<PayinEventType, string> = {
    [PayinEventType.TYPE_UNSPECIFIED]: 'Неизвестное событие',
    [PayinEventType.TYPE_ORDER_CREATED]: 'Ордер создан',
    [PayinEventType.TYPE_ORDER_UPDATED]: 'Ордер обновлён',
    [PayinEventType.TYPE_ORDER_FAILED]: 'Ошибка выполнения ордера',
    [PayinEventType.TYPE_ORDER_COMPLETED]: 'Ордер завершён',
    [PayinEventType.TYPE_ORDER_EXPIRED]: 'Срок ордера истёк',
    [PayinEventType.TYPE_TRADER_CANCELED]: 'Трейдер отменил ордер',
    [PayinEventType.TYPE_TRADER_CONFIRMED]: 'Трейдер подтвердил выполнение',
    [PayinEventType.TYPE_TRADER_APPEALED]: 'Трейдер открыл спор',
    [PayinEventType.TYPE_TRADER_ASKED_FOR_PROOFS]: 'Трейдер запросил доказательства',
    [PayinEventType.TYPE_MERCHANT_CREATED]: 'Мерчант создал ордер',
    [PayinEventType.TYPE_MERCHANT_ACQUIRED]: 'Мерчант принял платёж',
    [PayinEventType.TYPE_MERCHANT_RELEASED]: 'Мерчант отпустил средства',
    [PayinEventType.TYPE_MERCHANT_CANCELED]: 'Мерчант отменил ордер',
    [PayinEventType.TYPE_MERCHANT_CONFIRMED]: 'Мерчант подтвердил выполнение',
    [PayinEventType.TYPE_MERCHANT_PROVIDED_PROOFS]: 'Мерчант предоставил доказательства',
    [PayinEventType.TYPE_START_INVESTIGATION]: 'Начато расследование',
    [PayinEventType.TYPE_INVESTIGATION_APPROVED]: 'Расследование одобрено',
    [PayinEventType.TYPE_INVESTIGATION_DECLINED]: 'Расследование отклонено',
    [PayinEventType.TYPE_PAYOUT_INITIATED]: 'Инициирован вывод средств',
    [PayinEventType.TYPE_PAYOUT_FINALIZED]: 'Вывод средств завершён',
};

export const publicStatusColors: Record<PublicStatus, MantineColor> = {
    [PublicStatus.PUBLIC_STATUS_UNSPECIFIED]: 'gray.6',
    [PublicStatus.PUBLIC_STATUS_CREATED]: 'blue.5',
    [PublicStatus.PUBLIC_STATUS_PENDING]: 'orange.5',
    [PublicStatus.PUBLIC_STATUS_COMPLETED]: 'green.6',
    [PublicStatus.PUBLIC_STATUS_FAILED]: 'red.7',
} as const;

export const publicStatuses = [
    {
        value: PublicStatus.PUBLIC_STATUS_CREATED,
        label: 'Создан',
    },
    {
        value: PublicStatus.PUBLIC_STATUS_PENDING,
        label: 'В ожидании',
    },
    {
        value: PublicStatus.PUBLIC_STATUS_COMPLETED,
        label: 'Завершен',
    },
    {
        value: PublicStatus.PUBLIC_STATUS_FAILED,
        label: 'Ошибка',
    },
    {
        value: PublicStatus.PUBLIC_STATUS_UNSPECIFIED,
        label: 'Не указан',
    },
];
